<template>
  <!--
    🔴 **One bounded box containing the mobile nav, not a nav sitting outside one.**

    The nav used to be a sibling *above* this container, which made the document exactly one
    nav-bar taller than the viewport on mobile. That alone would be survivable, but `BaseLayout`'s
    root carries `overflow-hidden`, and an ancestor with `overflow: hidden` becomes the scrollport
    a descendant's `position: sticky` measures against — so a `sticky bottom-0` footer resolved
    "bottom" to the bottom of a box sitting a nav-bar below the fold and rendered there, exactly as
    if it were not sticky at all. Reported against the create-bulk-order wizard (CPTO, 2026-09-11):
    the footer only came fully into view once the nav had been scrolled away.

    `h-[calc(100dvh-60px)]` used to compensate for it and was replaced by `h-full` in #1541, which
    resolves to the whole viewport and loses the subtraction. This removes the magic number rather
    than restoring it — with the nav inside the box nothing overflows the viewport, so "bottom of
    the box" and "bottom of the screen" are the same edge again.

    The shape is `NavigationOnlyLayout`'s, which has always done it this way. The two navs are
    mutually exclusive — `MobileNavigation` is `sm:hidden`, `NavMenu` is `hidden sm:flex` — so this
    is nav-above-content as a column on mobile, and sidebar-beside-content as a row from `sm` up.
  -->
  <div class="flex h-dvh flex-col overscroll-none sm:flex-row">
    <!--
      The bar slides up out of the way as the user scrolls **down** and returns on the way **up**,
      reclaiming its height while they read (CPTO, 2026-09-11).

      It used to do this by accident: the bar sat outside the bounded box, so the document was one
      nav-bar taller than the viewport and scrolling simply pushed it off. That same overflow is
      what left every `sticky bottom-0` footer unstuck, so bounding the layout necessarily took the
      behaviour with it. This puts it back deliberately — and measured, rather than against the
      60px the old `calc()` assumed.

      `motion-reduce:transition-none` keeps the state change and drops the movement.
    -->
    <div
      ref="mobileNavBandRef"
      class="shrink-0 transition-[margin] duration-200 ease-out motion-reduce:transition-none sm:hidden"
      :style="mobileNavBandStyle"
    >
      <MobileNavigation
        v-if="!isMobileLandscape"
        :logo-url="authStore.loggedInUser?.selectedOrg?.logoUrl"
        :nav-items="navItems"
        :order-requests-count="ordersByOrgCount"
        :claim-requests-count="claimsByOrgCount"
        :return-requests-count="returnsByOrgCount"
        :org-name="orgName"
        :payment-requests-count="paymentRequestsByOrgCount"
        :credits-count="creditsTabShown ? creditsByOrgCount : 0"
        :client-variation-count="clientVariationsByOrgCount"
        :user-first-name="firstName"
        :user-email="userEmail"
        :user-name="userName"
        :show-settings-items="showSettingsItems"
      />
    </div>
    <NavMenu
      :logo-url="authStore.loggedInUser?.selectedOrg?.logoUrl"
      :nav-items="navItems"
      :order-requests-count="ordersByOrgCount"
      :claim-requests-count="claimsByOrgCount"
      :return-requests-count="returnsByOrgCount"
      :org-name="orgName"
      :payment-requests-count="paymentRequestsByOrgCount"
      :credits-count="creditsTabShown ? creditsByOrgCount : 0"
      :client-variation-count="clientVariationsByOrgCount"
      :user-first-name="firstName"
      :user-name="userName"
      :user-email="userEmail"
      :show-settings-items="showSettingsItems"
    />

    <div
      id="layout-container"
      :class="[
        // `min-h-0 flex-1` rather than `h-full`: on the mobile column the nav sits above this, so
        // `h-full` would be a whole viewport *under* a 60px bar and put the overflow straight
        // back. In the `sm:` row it is equivalent — the cross axis still stretches to full height.
        'bg-background-light-200 flex min-h-0 w-full flex-1 flex-col',
        { 'overflow-y-hidden': route.hash === '#BOQ' },
        // A view that scrolls inside itself must not sit in a scrolling parent: with both live
        // the two chain, and a scroll that reaches the inner end silently carries on in the outer
        // one. `min-h-0` below sizes this container correctly, but only removing its overflow
        // makes the chaining *impossible* rather than merely unlikely — which is why
        // `create-project` was already excluded, and why the bulk wizard joins it.
        { 'overflow-y-auto': !SELF_SCROLLING_ROUTES.has(route.name as string) },
        // 🔴 The horizontal twin of the `min-h-0` below, and it is scoped to the same routes for
        // the same reason. From `sm` up this is a flex item in a ROW, so its `min-width: auto`
        // resolves to its content's min-content width — which a view holding a wide horizontal
        // strip inflates, pushing the whole page (nav, header, footer and all) past the viewport
        // and getting worse as the sidebar widens. `min-w-0` lets it shrink to the space it was
        // given, so a view that scrolls inside itself does so in both axes.
        //
        // Deliberately NOT applied to every route: a page whose content is genuinely wider than
        // the viewport and has no scroll region of its own relies on exactly this overflow today,
        // and clipping it instead would be a regression. The self-scrolling views all bound their
        // own box already, so for them there is nothing left to clip.
        { 'min-w-0': SELF_SCROLLING_ROUTES.has(route.name as string) },
      ]"
    >
      <div :class="containerClasses">
        <Breadcrumb
          v-if="showGlobalBreadcrumb"
          :breadcrumb-items="getBreadcrumbItems"
          :is-loading="isBreadcrumbLoading"
          @item-clicked="navigate"
        />
      </div>
      <!--
        `min-h-0` is load-bearing for any view that bounds its own height and scrolls inside
        itself. Without it this wrapper's automatic minimum size is its *content's* height, so a
        routed view asking for `h-dvh`/`h-full` grew past `#layout-container` instead of fitting
        it: the view's own scroll region never engaged, `#layout-container` did the scrolling
        instead, and a `sticky bottom-0` footer inside the view rode 300-plus pixels below the
        fold. Measured, not guessed — the bulk-order wizard's footer was the symptom.

        Verified harmless to every ordinary view: a child with natural height is unaffected
        (`flex-1` already let the wrapper grow, and `min-h-0` only removes its refusal to shrink),
        so a tall page still overflows into `#layout-container`'s scroll exactly as before.
      -->
      <div
        :class="['flex min-h-0 flex-1 flex-col', { 'sm:px-4 sm:py-6': route.name === 'dashboard' }]"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import router from '@/router';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { computed, onMounted, onUnmounted, ref, toRefs } from 'vue';

// Stores
import { useCan } from '@/composables/use-can';
import { Actions, Subjects } from '@boqnet/permissions';
import { useTotalsStore } from '@/stores/utils/totals.store';
import { useAuthStore } from '@/stores/auth.store';
import { useOrdersStore } from '@/stores/orders.store';
import { useOrgStore } from '@/stores/organisations.store';
import { useCreditRequestStore } from '@/stores/credit-requests.store';
// Types
import type { Breadcrumb as BreadcrumbType } from '@/stores/utils/breadcrumb.store';
import { useBreadcrumbStore } from '@/stores/utils/breadcrumb.store';
import { useClientVariationStore } from '@/stores/client-variation.store';

// Types

import NavMenu from '@/components/UiLibrary/Menu/NavMenu.vue';
import { useNavItems } from '@/composables/use-nav-items';

// Components
import Breadcrumb from '@/components/UiLibrary/Menu/Breadcrumb.vue';
import MobileNavigation from '@/components/Navigation/MobileNavigation.vue';
import { useHideOnScroll } from '@/composables/use-hide-on-scroll';

// Utilities
import { selectedOrgId } from '@/common/utils/role.utils';
import { BULK_ORDER_WIZARD_ROUTE_NAMES } from '@/stores/bulk-order-wizard.store';
import { useReturnsStore } from '@/stores/returns.store';
import { userIdentity } from '@/common/utils/user-identity';

const { navItems } = useNavItems();

const orgStore = useOrgStore();
const authStore = useAuthStore();
const { can } = useCan();
const ordersStore = useOrdersStore();
const returnStore = useReturnsStore();
const breadcrumbStore = useBreadcrumbStore();
const clientVariationStore = useClientVariationStore();
const creditRequestStore = useCreditRequestStore();

const { items, isLoading: isBreadcrumbLoading } = storeToRefs(breadcrumbStore);
const { loggedInUser } = storeToRefs(authStore);
const {
  paymentRequestsByOrgCount,
  ordersByOrgCount,
  claimsByOrgCount,
  returnsByOrgCount,
  clientVariationsByOrgCount,
  creditsByOrgCount,
} = toRefs(useTotalsStore());

// --- Accounts nav badge: payment requests (+ credits for non-AP roles) — PE-213 ---
// Context: org finance, org admin and the BOQ tier keep the Accounts Payable tab and never get a
// separate Credits tab, so the audience is "holds credits, but has no AP tab to hold them" rather
// than a role list (PE-213).
const creditsTabShown = computed(
  () => can(Actions.LIST, Subjects.CREDIT) && !can(Actions.LIST, Subjects.ACCOUNTS_PAYABLE),
);

// Fetch the org-scoped pending credits count into the totals store (`creditsByOrgCount`)
// for the Accounts nav badge. Org-scoped → no accountsPayableId; the BE scopes the
// result to the user's sites/projects. Mirrors AccountsLayout's credits-count fetch.
const fetchCreditsNavCount = async () => {
  // Only fetch when the Credits tab applies to this user (the badge is gated the
  // same way), so non-Credits/flag-off users don't issue an org-scoped credits
  // request. Snapshot/restore the shared store filters so this nav-level fetch
  // doesn't leave creditRequestStore scoped for other views. (PE-213)
  if (!creditsTabShown.value || !selectedOrgId.value) return;

  const previousFilters = { ...creditRequestStore.filters };
  try {
    creditRequestStore.filters.organisationId = selectedOrgId.value;
    creditRequestStore.filters.accountsPayableId = null;
    await creditRequestStore.fetchCount();
  } finally {
    Object.assign(creditRequestStore.filters, previousFilters);
  }
};

/**
 * Routes whose view owns its own scroll region and must therefore not be scrolled by
 * `#layout-container` as well. Both being scrollable is what produces scroll chaining.
 */
const SELF_SCROLLING_ROUTES = new Set(['create-project', ...BULK_ORDER_WIZARD_ROUTE_NAMES]);

const route = useRoute();

const orgName = computed(() => loggedInUser.value?.selectedOrg?.name || '');

const showSettingsItems = computed(() => userIdentity.seesSettingsMenu());

// The avatar derives its own initials, so it wants the name rather than an "AB" computed here —
// feeding it initials would have it take the first letter of those and show one.
const userName = computed(() =>
  [loggedInUser.value?.firstName, loggedInUser.value?.lastName].filter(Boolean).join(' '),
);

const firstName = computed(() => loggedInUser.value?.firstName);

const userEmail = computed(() => loggedInUser.value?.email);

// The band the bar sits in, not the bar itself — the component already owns a ref here.
const { targetRef: mobileNavBandRef, targetStyle: mobileNavBandStyle } = useHideOnScroll({
  // Navigation can swap the inner scroller while this layout stays mounted, and a short
  // destination page emits no scroll event — so without this the bar can stay hidden on a page
  // with nothing left to scroll.
  resetOn: () => route.fullPath,
});

const isMobileLandscape = ref(false);

const checkOrientation = () => {
  const width = window.innerWidth;
  const height = window.innerHeight;

  isMobileLandscape.value = width > height && width < 1024;
};

const showGlobalBreadcrumb = computed(() => {
  const name = route.name as string;

  const alwaysShow = [
    // Accounts
    'accounts-payable',
    'payment-requests',
    'accounts-credits',
    'account-details-payment-requests',
    'account-details-orders',
    'account-details-claims',
    'account-details-returns',
    'account-details-credits',
    'account-details-statements',
    'account-details-transaction-log',
    // Vendors / Orders / Claims list pages
    'vendors',
    'orders',
    'claims',
  ];

  // Project routes that always show breadcrumb
  const projectRoutes = [
    'projects',
    'create-project',
    'create-site',
    'sites',
    'create-client-variation',
    'variations',
    'project-cashflow',
    'site-cashflow',
    'line-item-history',
    'generate-claim-sheet',
  ];

  return alwaysShow.includes(name) || projectRoutes.includes(name);
});

const getBreadcrumbItems = computed(() => {
  const breadcrumbItems = [...items.value];

  if (route.name === 'create-client-variation') {
    breadcrumbItems.push({
      label: 'Create client variation',
      to: '',
      index: breadcrumbItems.length,
      isClickable: false,
    });
  }

  if (route.name === 'variations') {
    breadcrumbItems.push({
      label: 'Variations',
      to: '',
      index: breadcrumbItems.length,
      isClickable: false,
    });
  }

  return breadcrumbItems;
});

const navigate = (item: BreadcrumbType) => {
  if (item.to) {
    router.push(item.to);
    items.value.splice(item.index + 1);
  }
};

const containerClasses = computed(() => {
  const specialRoutes = ['dashboard', 'no-user-orgs', 'no-projects-or-sites', 'maintenance'];
  const excludedRoutes = [
    'create-project',
    'boq',
    'organisation-details',
    'create-custom-vendor',
    'create-boq-vendor',
    'vendor-items',
    'design-system',
    'confirm-order-requests',
    'confirm-claim-requests',
    'create-new-order',
    'create-new-claim',
    // Every bulk wizard step, for the same reason as every create flow above it: the wizard owns
    // its whole frame through its own nested `BaseLayout`, whose root is `sm:h-dvh`. Padding above
    // that block pushes its `sticky bottom-0` footer the same distance below the fold, so the
    // wizard's Next button sat off-screen until you scrolled `#layout-container`.
    ...BULK_ORDER_WIZARD_ROUTE_NAMES,
    'create-client-variation',
    'projects',
    'vendors',
  ];

  return [
    specialRoutes.includes(route.name as string) ? 'bg-none' : 'bg-white',
    excludedRoutes.includes(route.name as string) ? '' : 'px-4 pt-3 sm:pt-6',
  ];
});

onUnmounted(() => {
  window.removeEventListener('resize', checkOrientation);
});

onMounted(() => {
  setTimeout(() => {
    if (!userIdentity.isBareOrgMember() && userIdentity.seesStaffNavigation()) {
      orgStore.getPaymentRequestCount(selectedOrgId.value!, {
        status: 'Requested',
      });
      ordersStore.getOrderTotals({ orgId: selectedOrgId.value!, type: 'Order' }, 'orders');
      ordersStore.getOrderTotals({ orgId: selectedOrgId.value!, type: 'Claim' }, 'claims');
      orgStore.getOrgDetails();
      fetchCreditsNavCount();
      returnStore.getReturnsCount({
        organisationId: selectedOrgId.value!,
      });
    }
  }, 500);
  if (userIdentity.isClientAnywhere()) {
    clientVariationStore.getClientVariationCount(selectedOrgId.value!);
  }
  window.addEventListener('resize', checkOrientation);
  checkOrientation();
});
</script>
<style scoped>
@reference "tailwindcss";

@keyframes p-progress-spinner-color {
  100%,
  0% {
    stroke: #0a37c3;
  }
  40% {
    stroke: #0a37c3;
  }
  66% {
    stroke: #0a37c3;
  }
  80%,
  90% {
    stroke: #0a37c3;
  }
}
</style>
